/**
 * Tenant configuration loader
 *
 * Reads VITE_TENANT environment variable and loads the appropriate tenant config.
 * The config is loaded at build time and baked into the bundle.
 */

import type { TenantConfig, TenantId } from './types'

// Import tenant configs statically for build-time resolution
import uicConfig from './tenants/uic'
import arizonaConfig from './tenants/arizona'
import usfConfig from './tenants/usf'

/** Map of tenant ID to config */
const tenantConfigs: Record<TenantId, TenantConfig> = {
  uic: uicConfig,
  arizona: arizonaConfig,
  usf: usfConfig,
} as Record<TenantId, TenantConfig>

/** Get the current tenant ID from environment */
function getTenantId(): TenantId {
  const tenant = import.meta.env.VITE_TENANT as string | undefined

  if (!tenant) {
    console.warn('VITE_TENANT not set, defaulting to "arizona"')
    return 'arizona'
  }

  if (!(tenant in tenantConfigs)) {
    throw new Error(
      `Unknown tenant "${tenant}". Available tenants: ${Object.keys(tenantConfigs).join(', ')}`
    )
  }

  return tenant as TenantId
}

/** Cached config instance */
let cachedConfig: TenantConfig | null = null

/**
 * Get the current tenant configuration
 *
 * @returns The tenant configuration for the current VITE_TENANT
 * @throws Error if VITE_TENANT is set to an unknown tenant
 */
export function getConfig(): TenantConfig {
  if (!cachedConfig) {
    const tenantId = getTenantId()
    cachedConfig = tenantConfigs[tenantId]
  }
  return cachedConfig
}

/**
 * Get the current tenant ID
 */
export function getCurrentTenantId(): TenantId {
  return getTenantId()
}

/**
 * Check if current tenant uses LARX data source
 */
export function usesLarxDataSource(): boolean {
  return getConfig().dataSource === 'larx'
}

/**
 * Check if current tenant uses Arizona S3 data source
 */
export function usesArizonaS3DataSource(): boolean {
  return getConfig().dataSource === 'arizona-s3'
}

// Re-export types for convenience
export type { TenantConfig, TenantId } from './types'
